import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import apiClient from '../api/client'

type ModelCodename = string  // Dynamic - loaded from API
type GenerationStatus = 'idle' | 'generating' | 'success' | 'error'
type ResolutionOption = '2k' | '2k_portrait' | '4k' | '4k_portrait'
type AspectRatioOption = 'none' | '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '4:5' | '5:4' | '2:3' | '3:2' | '21:9'
type ImageSizeOption = '1K' | '2K' | '4K'

interface ModelInfo {
  name: string
  description?: string
  max_files: number
  status: string
  avg_generation_time?: number
  features?: string[]
  input_type?: string
  requires_prompt?: boolean
}

interface GenerationResult {
  status: GenerationStatus
  previewUrls?: string[]
  message?: string
  imagesGenerated?: number
  metadata?: any
}

interface EasyWayState {
  modelImage: File | null
  garmentImage: File | null
  accessory1: File | null  // bag
  accessory2: File | null  // glasses
  selectedModel: ModelCodename
  resolution: ResolutionOption
  aspectRatio: AspectRatioOption  // NEW - Gemini aspect ratio
  imageSize: ImageSizeOption  // NEW - Gemini Pro resolution (1K/2K/4K)
  prompt: string
  generationResult: GenerationResult
  availableModels: Record<string, ModelInfo>  // NEW - loaded from API
  modelsLoading: boolean  // NEW
}

interface EasyWayContextType extends EasyWayState {
  setFile: (slot: 'modelImage' | 'garmentImage' | 'accessory1' | 'accessory2', file: File | null) => void
  setSelectedModel: (model: ModelCodename) => void
  setResolution: (resolution: ResolutionOption) => void
  setAspectRatio: (ratio: AspectRatioOption) => void  // NEW
  setImageSize: (size: ImageSizeOption) => void  // NEW
  setPrompt: (text: string) => void
  generateImage: (projectName?: string) => Promise<boolean>
  resetState: () => void
  clearSlot: (slot: 'modelImage' | 'garmentImage' | 'accessory1' | 'accessory2') => void
  isTextToImageModel: (modelCodename: string) => boolean  // NEW - check if model is text-to-image only
}

const EasyWayContext = createContext<EasyWayContextType | undefined>(undefined)

export const useEasyWay = () => {
  const context = useContext(EasyWayContext)
  if (context === undefined) {
    throw new Error('useEasyWay must be used within EasyWayProvider')
  }
  return context
}

interface EasyWayProviderProps {
  children: ReactNode
}

export const EasyWayProvider = ({ children }: EasyWayProviderProps) => {
  const [modelImage, setModelImage] = useState<File | null>(null)
  const [garmentImage, setGarmentImage] = useState<File | null>(null)
  const [accessory1, setAccessory1] = useState<File | null>(null)
  const [accessory2, setAccessory2] = useState<File | null>(null)
  const [selectedModel, setSelectedModel] = useState<ModelCodename>('strawberry')
  const [resolution, setResolution] = useState<ResolutionOption>('2k')
  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>('none')  // NEW - Gemini aspect ratio
  const [imageSize, setImageSize] = useState<ImageSizeOption>('1K')  // NEW - Gemini Pro resolution
  const [prompt, setPrompt] = useState<string>('Dress the model')
  const [generationResult, setGenerationResult] = useState<GenerationResult>({
    status: 'idle'
  })
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo>>({})  // NEW
  const [modelsLoading, setModelsLoading] = useState<boolean>(true)  // NEW

  // Load available models from API
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await apiClient.get('/easy_way/models')
        if (response.data.status === 'success') {
          setAvailableModels(response.data.models || {})
          // Auto-select first available model if current selection is not available
          const modelKeys = Object.keys(response.data.models || {})
          if (modelKeys.length > 0 && !response.data.models[selectedModel]) {
            setSelectedModel(modelKeys[0])
          }
        }
      } catch (error) {
        console.error('Failed to load models:', error)
      } finally {
        setModelsLoading(false)
      }
    }
    loadModels()
  }, [])

  const setFile = (
    slot: 'modelImage' | 'garmentImage' | 'accessory1' | 'accessory2',
    file: File | null
  ) => {
    switch (slot) {
      case 'modelImage':
        setModelImage(file)
        break
      case 'garmentImage':
        setGarmentImage(file)
        break
      case 'accessory1':
        setAccessory1(file)
        break
      case 'accessory2':
        setAccessory2(file)
        break
    }
  }

  const clearSlot = (slot: 'modelImage' | 'garmentImage' | 'accessory1' | 'accessory2') => {
    setFile(slot, null)
  }

  // Check if a model is text-to-image only (no file uploads)
  const isTextToImageModel = (modelCodename: string): boolean => {
    const model = availableModels[modelCodename]
    if (!model) return false
    return model.max_files === 0 || model.input_type === 'text_only'
  }

  const generateImage = async (projectName: string = 'Default'): Promise<boolean> => {
    try {
      const isTextOnly = isTextToImageModel(selectedModel)

      // Validate inputs based on model type
      if (isTextOnly) {
        // Text-to-image: require non-empty prompt
        if (!prompt || prompt.trim().length === 0) {
          setGenerationResult({
            status: 'error',
            message: 'Please provide a text description for generation'
          })
          return false
        }
      } else {
        // Image-to-image: require at least one file
        if (!modelImage && !garmentImage && !accessory1 && !accessory2) {
          setGenerationResult({
            status: 'error',
            message: 'Please upload at least one image'
          })
          return false
        }
      }

      setGenerationResult({ status: 'generating' })

      // Build FormData
      const formData = new FormData()

      if (modelImage) formData.append('model_image', modelImage)
      if (garmentImage) formData.append('garment_image', garmentImage)
      if (accessory1) formData.append('accessory1', accessory1)
      if (accessory2) formData.append('accessory2', accessory2)

      formData.append('selected_model', selectedModel)
      formData.append('resolution', resolution)
      formData.append('aspect_ratio', aspectRatio)  // NEW - Gemini aspect ratio
      formData.append('image_size', imageSize)  // NEW - Gemini Pro resolution
      formData.append('prompt', prompt)
      formData.append('project_name', projectName)

      // Call API (without /api prefix since apiClient.baseURL already includes it)
      const response = await apiClient.post('/easy_way/generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000  // 2 minutes timeout
      })

      const data = response.data

      if (data.success) {
        setGenerationResult({
          status: 'success',
          previewUrls: data.preview_files || [],
          imagesGenerated: data.images_generated || 0,
          message: data.message || 'Generation completed',
          metadata: data.metadata
        })
        return true
      } else {
        setGenerationResult({
          status: 'error',
          message: data.message || 'Generation failed'
        })
        return false
      }

    } catch (error: any) {
      console.error('Foto Sesja generation error:', error)

      let errorMessage = 'Generation failed'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      setGenerationResult({
        status: 'error',
        message: errorMessage
      })
      return false
    }
  }

  const resetState = () => {
    setModelImage(null)
    setGarmentImage(null)
    setAccessory1(null)
    setAccessory2(null)
    setSelectedModel('strawberry2')
    setResolution('2k')
    setAspectRatio('1:1')  // NEW
    setImageSize('1K')  // NEW
    setPrompt('Dress the model')
    setGenerationResult({ status: 'idle' })
  }

  const value: EasyWayContextType = {
    modelImage,
    garmentImage,
    accessory1,
    accessory2,
    selectedModel,
    resolution,
    aspectRatio,  // NEW
    imageSize,  // NEW
    prompt,
    generationResult,
    availableModels,  // NEW
    modelsLoading,  // NEW
    setFile,
    setSelectedModel,
    setResolution,
    setAspectRatio,  // NEW
    setImageSize,  // NEW
    setPrompt,
    generateImage,
    resetState,
    clearSlot,
    isTextToImageModel  // NEW
  }

  return (
    <EasyWayContext.Provider value={value}>
      {children}
    </EasyWayContext.Provider>
  )
}
